import React, { useState, useEffect } from 'react';
import { X, Loader2, IndianRupee, Zap, CheckCircle2 } from 'lucide-react';
import { paymentsAPI } from '@/services/paymentsAPI';
import { toast } from 'sonner';

/**
 * RazorpayCheckoutModal
 *
 * Opens a modal where the user picks an INR amount, then launches the
 * Razorpay Standard Checkout modal. On success, calls /api/payments/verify
 * to credit Mu points server-side.
 *
 * Props:
 *   - isOpen (bool)
 *   - onClose (fn)
 *   - user   ({ email, phone, name? })  — used for Razorpay prefill
 *   - onSuccess (fn) — called with { mu_points_credited, new_balance, amount_inr }
 */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function RazorpayCheckoutModal({ isOpen, onClose, user, onSuccess }) {
  const [amount, setAmount] = useState('10');
  const [config, setConfig] = useState(null); // { inr_per_mu, min_recharge_inr }
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState('idle'); // idle | creating | checkout | verifying | success | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setState('idle');
    setError('');
    setResult(null);
    (async () => {
      try {
        const c = await paymentsAPI.getConfig();
        setConfig(c);
        setAmount(String(c.min_recharge_inr));
      } catch (e) {
        setError('Could not load conversion rate. Please try again.');
      }
    })();
  }, [isOpen]);

  if (!isOpen) return null;

  const rate = Number(config?.inr_per_mu || 1);
  const minAmt = Number(config?.min_recharge_inr || 10);
  const amt = Number(amount) || 0;
  const muPoints = rate > 0 ? Math.floor(amt / rate) : 0;

  const pay = async () => {
    setError('');
    if (amt < minAmt) {
      setError(`Minimum recharge is ₹${minAmt}`);
      return;
    }
    setState('creating');
    setLoading(true);
    try {
      const order = await paymentsAPI.createOrder(amt);
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error('Razorpay checkout failed to load. Please try again.');

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Numuv',
        description: `Recharge ${order.mu_points} Mu points`,
        order_id: order.order_id,
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#10b981' },
        modal: {
          ondismiss: () => {
            setState('idle');
            setLoading(false);
          },
        },
        handler: async (response) => {
          setState('verifying');
          try {
            const v = await paymentsAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setResult(v);
            setState('success');
            setLoading(false);
            try { window.dispatchEvent(new Event('mu-wallet-changed')); } catch (_) {}
            toast.success(`${v.mu_points_credited} Mu points credited!`);
            if (onSuccess) onSuccess(v);
          } catch (e) {
            setState('error');
            setError(e?.response?.data?.detail || 'Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
      };
      setState('checkout');
      setLoading(false);
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (e) => {
        setState('error');
        setError(e?.error?.description || 'Payment failed');
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      setState('error');
      setError(e?.response?.data?.detail || e.message || 'Could not start checkout');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="razorpay-checkout-modal">
      <div className="w-full max-w-md bg-white dark:bg-[#0f0f12] border border-slate-200 dark:border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-zinc-800">
          <h3 className="text-slate-900 dark:text-white font-semibold flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-emerald-500" /> Recharge Mu Points
          </h3>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white"
                  data-testid="razorpay-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {state === 'success' ? (
            <div className="text-center py-4" data-testid="razorpay-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-slate-900 dark:text-white font-semibold text-lg mb-1">
                {result?.mu_points_credited} Mu credited
              </p>
              <p className="text-slate-500 dark:text-zinc-500 text-sm">New balance: {result?.new_balance} Mu</p>
              <button onClick={onClose} className="mt-4 w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium">
                Done
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs text-slate-500 dark:text-zinc-500 uppercase tracking-wider">Amount (INR)</label>
                <div className="mt-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-zinc-500">₹</span>
                  <input
                    type="number"
                    min={minAmt}
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-slate-50 dark:bg-black border border-slate-200 dark:border-zinc-800 rounded-lg text-slate-900 dark:text-white text-lg font-medium focus:outline-none focus:border-emerald-500/50"
                    data-testid="razorpay-amount-input"
                  />
                </div>
                <div className="mt-2 flex gap-2">
                  {[10, 50, 100, 500].map((v) => (
                    <button key={v} onClick={() => setAmount(String(v))} type="button"
                      className="px-3 py-1 text-xs rounded-full bg-slate-100 dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-zinc-300 hover:border-emerald-500/40"
                      data-testid={`razorpay-quick-${v}`}>₹{v}</button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-zinc-400">You'll receive</span>
                  <span className="text-emerald-700 dark:text-emerald-300 font-semibold flex items-center gap-1" data-testid="razorpay-mu-preview">
                    <Zap className="w-3 h-3" /> {muPoints} Mu
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-slate-500 dark:text-zinc-500">Current rate</span>
                  <span className="text-slate-600 dark:text-zinc-400">₹{rate} = 1 Mu</span>
                </div>
                <p className="mt-2 text-[11px] text-slate-500 dark:text-zinc-500" data-testid="razorpay-min-note">
                  Note: minimum recharge is <span className="font-medium text-slate-700 dark:text-zinc-300">₹{minAmt}</span>.
                </p>
              </div>

              {error && (
                <p className="text-xs text-red-600 dark:text-red-400" data-testid="razorpay-error">{error}</p>
              )}

              <button
                onClick={pay}
                disabled={loading || amt < minAmt}
                className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="razorpay-pay-btn"
              >
                {loading || state === 'verifying' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {state === 'verifying' ? 'Verifying…' : 'Loading…'}</>
                ) : (
                  <>Pay ₹{amt || 0}</>
                )}
              </button>
              <p className="text-[10px] text-center text-slate-400 dark:text-zinc-600">
                Secured by Razorpay. Cards, UPI, netbanking supported.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
